import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useOutletContext } from "react-router-dom";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import axios from "axios";
import toastr from "toastr";
import { MaterialReactTable } from "material-react-table";
import { Client_View } from "../../utils/sweetalert";

const API = process.env.REACT_APP_API_BASE_URL;

// ─── Default row shape ────────────────────────────────────────────────────────
const defaultRow = () => ({
  file_id: null,
  bill_date: "",
  reimbursement_type_id: "",
  remarks: "",
  billFile: null,
  billFileName: "",
  amount: "",
  currency_id: "",
  conversion_rate: "",
  entity_amount: "",
});

// ─── Default form values ──────────────────────────────────────────────────────
const defaultValues = () => ({
  expense_id: "",
  employeeinfo_has_travel_id: "",
  subject: "",
  authorised_by: "",
  otherFile: null,
  otherFileName: "",
  rows: [defaultRow()],
});

export default function ReimbursementView({ selfView = true }) {
  const { setGlobalLoading, token } = useOutletContext();

  // ── Listing state ─────────────────────────────────────────────────────────
  const [statusTab, setStatusTab] = useState("pending");
  const [reimbursements, setReimbursements] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // ── Camera state ──────────────────────────────────────────────────────────
  //   cameraRowIndex tells us WHICH bill row opened the camera, so the captured
  //   photo gets written back into the correct row. null = camera closed.
  const [cameraRowIndex, setCameraRowIndex] = useState(null);

  // ── Dropdown options ──────────────────────────────────────────────────────
  const [formOptions, setFormOptions] = useState({
    expenses: [],
    reimbursementTypes: [],
    currencies: [],
    users: [],
    travels: [],
  });

  // ── react-hook-form setup ─────────────────────────────────────────────────
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: defaultValues() });

  const { fields, append, remove } = useFieldArray({ control, name: "rows" });

  // Watch expense_id to toggle Travel/Subject field
  const watchedExpenseId = watch("expense_id");
  const isTravel = watchedExpenseId == 2;

  // ── FIX: Use useWatch instead of watch("rows") for totalAmount.
  //   watch("rows") inside useMemo does NOT reliably re-trigger the memo when
  //   individual row fields change via setValue (React Hook Form batches updates).
  //   useWatch subscribes at the component level and always reflects live values.
  const watchedRows = useWatch({ control, name: "rows" });

  const totalAmount = useMemo(() => {
    if (!watchedRows) return 0;
    return watchedRows.reduce((sum, r) => {
      const val = parseFloat(r?.entity_amount);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }, [watchedRows]);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: "Bearer " + token } }),
    [token],
  );

  // ── Camera capture handler ────────────────────────────────────────────────
  //   Receives a File from <CameraCapture> and writes it into the row that
  //   opened the camera — identical to what the file picker's onChange does.
  const handleCameraCapture = (file) => {
    if (cameraRowIndex === null) return;
    setValue(`rows.${cameraRowIndex}.billFile`, file, { shouldValidate: true });
    setValue(`rows.${cameraRowIndex}.billFileName`, file.name);
    setCameraRowIndex(null);
  };

  // ─── Fetch listing ──────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/reimbursements?status=${statusTab}&self=${selfView}`,
        authHeaders,
      );
      console.log(data.data);

      setReimbursements(data.data || []);
    } catch (err) {
      console.error("[fetchList] ERROR:", err);
      toastr.error("Failed to load reimbursements.");
    } finally {
      setListLoading(false);
    }
  }, [statusTab, selfView, authHeaders]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ─── Fetch dropdown options ─────────────────────────────────────────────────
  const fetchFormOptions = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/reimbursements/form-data`,
        authHeaders,
      );
      setFormOptions(data.data);
    } catch (err) {
      console.error("[fetchFormOptions] ERROR:", err);
      toastr.error("Failed to load form options.");
    }
  }, [authHeaders]);

  // ─── Open modal for create ──────────────────────────────────────────────────
  const openCreate = async () => {
    setEditId(null);
    reset(defaultValues());
    await fetchFormOptions();
    setShowModal(true);
  };

  // ─── Open modal for edit ────────────────────────────────────────────────────
  const openEdit = async (id) => {
    setFormLoading(true);
    await fetchFormOptions();
    try {
      const { data } = await axios.get(
        `${API}/reimbursements/${id}`,
        authHeaders,
      );
      const { reimbursement, files } = data.data;
      setEditId(id);

      reset({
        expense_id: reimbursement.expense_id || "",
        employeeinfo_has_travel_id:
          reimbursement.employeeinfo_has_travel_id || "",
        subject: reimbursement.subject || "",
        authorised_by: reimbursement.authorised_by || "",
        otherFile: null,
        otherFileName: reimbursement.filename || "",
        rows: files.map((f) => ({
          file_id: f.id,
          bill_date: f.bill_date || "",
          reimbursement_type_id: f.reimbursement_type_id || "",
          remarks: f.remarks || "",
          billFile: null,
          billFileName: f.filename || "",
          amount: f.amount || "",
          currency_id: f.currency_id || "",
          conversion_rate: f.conversion_rate || "",
          entity_amount: f.entity_amount || "",
        })),
      });
      setShowModal(true);
    } catch (err) {
      console.error("[openEdit] ERROR:", err);
      toastr.error("Failed to load reimbursement details.");
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setCameraRowIndex(null); // ensure camera never lingers behind a closed form
    reset(defaultValues());
  };

  // ─── Fetch conversion rate for a row ───────────────────────────────────────
  const fetchConversionRate = async (index, currencyId, amount) => {
    if (!currencyId || !amount) return;
    try {
      const { data } = await axios.get(
        `${API}/reimbursements/conversion-rate?currency_id=${currencyId}`,
        authHeaders,
      );
      const rate = data.rate || 1;
      const converted = Math.round(parseFloat(amount) * rate);
      setValue(`rows.${index}.conversion_rate`, rate, { shouldDirty: true });
      setValue(`rows.${index}.entity_amount`, converted, { shouldDirty: true });
    } catch (err) {
      console.error("[fetchConversionRate] ERROR:", err);
      toastr.warning("Could not fetch conversion rate.");
    }
  };

  // ─── Submit handler ─────────────────────────────────────────────────────────
  const onSubmit = async (formValues) => {
    const hasFile = formValues.rows.some((r) => r.billFile || r.file_id);
    if (!hasFile) {
      toastr.error("Please attach at least one bill file.");
      return;
    }

    setGlobalLoading(true);
    try {
      const fd = new FormData();

      // Top-level fields (no [] needed — single values)
      fd.append("expense_id", formValues.expense_id);
      fd.append(
        "employeeinfo_has_travel_id",
        formValues.employeeinfo_has_travel_id,
      );
      fd.append("subject", formValues.subject);
      fd.append("authorised_by", formValues.authorised_by);

      if (formValues.otherFile) {
        fd.append("otherfile", formValues.otherFile);
      }

      // ── FIX: Send row fields WITHOUT [] brackets.
      //   Multer strips [] from multipart field names before populating req.body.
      //   Sending "bill_date" (no brackets) is what the backend actually receives,
      //   so align frontend and backend on the same key names.
      //   When there is only ONE row, FormData will deliver a single string value;
      //   when there are MULTIPLE rows the same key appears multiple times and
      //   multer collects them into an array. toArr() on the backend handles both.
      formValues.rows.forEach((row, idx) => {
        fd.append("file_id", row.file_id ?? "");
        fd.append("bill_date", row.bill_date);
        fd.append("reimbursement_type_id", row.reimbursement_type_id);
        fd.append("remarks", row.remarks);
        fd.append("amount", row.amount);
        fd.append("currency_id", row.currency_id);
        fd.append("conversion_rate", row.conversion_rate);
        fd.append("entity_amount", row.entity_amount);

        if (row.billFile) {
          fd.append("billfiles", row.billFile);
          fd.append("billfile_row_index", idx);
        }
      });

      const config = { headers: { Authorization: "Bearer " + token } };

      if (editId) {
        const res = await axios.put(
          `${API}/reimbursements/${editId}`,
          fd,
          config,
        );
        console.log("[onSubmit] PUT response:", res.data);
        toastr.success("Reimbursement updated successfully.");
      } else {
        const res = await axios.post(`${API}/reimbursements`, fd, config);
        console.log("[onSubmit] POST response:", res.data);
        toastr.success("Reimbursement submitted successfully.");
      }
      closeModal();
      fetchList();
    } catch (err) {
      console.error("[onSubmit] ERROR:", err);
      console.error("[onSubmit] ERROR response data:", err?.response?.data);
      toastr.error(
        err?.response?.data?.message || "Failed to save reimbursement.",
      );
    } finally {
      setGlobalLoading(false);
    }
  };

  const confirmDelete = (id, onConfirmDelete) => {
    Client_View({
      title: "Are you sure?",
      text: "This reimbursement will be permanently deleted.",
      icon: "warning",
      confirmButtonText: "Yes, Delete",
      confirmCallback: () => onConfirmDelete(id),
      cancleCallback: () => {
        console.log("User cancelled deletion");
      },
    });
  };

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    setGlobalLoading(true);

    try {
      await axios.delete(`${API}/reimbursements/${id}`, authHeaders);

      toastr.success("Reimbursement deleted.");
      fetchList();
    } catch (err) {
      console.error("[handleDelete] ERROR:", err);
      toastr.error(err?.response?.data?.message || "Failed to delete.");
    } finally {
      setGlobalLoading(false);
    }
  };

  const viewFile = async (savename) => {
    const response = await fetch(
      `${API}/files/mediaReimbursement/${savename}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    window.open(url, "_blank");
  };

  // ─── MRT columns ─────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      { accessorKey: "employee_name", header: "Name", size: 160 },
      {
        accessorKey: "subject",
        header: "Subject",
        size: 180,
        Cell: ({ row }) =>
          row.original.subject || row.original.travel_details || "—",
      },
      { accessorKey: "entity_name", header: "Entity", size: 130 },
      { accessorKey: "expense_name", header: "Expense Type", size: 140 },
      {
        accessorKey: "final_amount",
        header: "Amount",
        size: 110,
        Cell: ({ row }) =>
          (row.original.currency_symbol || "") +
          " " +
          (row.original.final_amount || 0),
      },
      { accessorKey: "authorised_by_name", header: "Authorised By", size: 150 },
      {
        accessorKey: "created_date",
        header: "Submit Date",
        size: 120,
        Cell: ({ cell }) => {
          const d = cell.getValue();
          return d ? new Date(d).toLocaleDateString("en-GB") : "—";
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => {
          const s = cell.getValue();
          const map = {
            pending: "warning",
            paid: "success",
            "not-paid": "danger",
            hold: "secondary",
          };
          return (
            <span
              className={`badge bg-${map[s] || "secondary"} text-capitalize`}
            >
              {s}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Action",
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => {
          const rec = row.original;
          return (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                title="Edit"
                onClick={() => openEdit(rec.id)}
              >
                <i className="ti ti-edit fs-14" />
              </button>
              <button
                onClick={() => viewFile(rec.file_savename)}
                className="btn btn-sm btn-outline-info"
              >
                <i className="ti ti-eye" />
              </button>
              {rec.status === "pending" && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  title="Delete"
                  onClick={() => confirmDelete(rec.id, handleDelete)}
                >
                  <i className="ti ti-trash fs-14" />
                </button>
              )}
            </div>
          );
        },
      },
      // eslint-disable-next-line
    ],
    [],
  );

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Breadcrumb ─────────────────────────────────────────────────────── */}
      <div className="d-md-flex d-block align-items-center justify-content-end page-breadcrumb mb-3">
        <button className="btn btn-primary" onClick={openCreate}>
          <i className="ti ti-plus me-1" />
          Add Reimbursement
        </button>
      </div>

      {/* ── Status tabs + MRT table ─────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header border-0 pb-0">
          <ul className="nav nav-tabs nav-tabs-bottom">
            {["pending", "paid", "not-paid", "hold", "all"].map((tab) => (
              <li className="nav-item" key={tab}>
                <button
                  className={`nav-link text-capitalize ${statusTab === tab ? "active" : ""}`}
                  onClick={() => setStatusTab(tab)}
                >
                  {tab}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body p-0">
          <MaterialReactTable
            columns={columns}
            data={reimbursements}
            state={{ isLoading: listLoading }}
            enableStickyHeader
            muiTableContainerProps={{ sx: { maxHeight: "500px" } }}
            initialState={{ density: "compact" }}
          />
        </div>
      </div>

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              {/* Header */}
              <div className="modal-header">
                <h5 className="modal-title">
                  {editId ? "Edit Reimbursement" : "Apply Reimbursement"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                />
              </div>

              {/* Body */}
              <div className="modal-body">
                {formLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : (
                  <form
                    id="reimbursement-form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                  >
                    {/* ── Top fields ──────────────────────────────────────── */}
                    <div className="row g-3 mb-3">
                      {/* Expense Type */}
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">
                          Expense Type <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${errors.expense_id ? "is-invalid" : ""}`}
                          {...register("expense_id", {
                            required: "Expense type is required",
                          })}
                        >
                          <option value="">Select Expense Type</option>
                          {formOptions.expenses.map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.name}
                            </option>
                          ))}
                        </select>
                        {errors.expense_id && (
                          <div className="invalid-feedback">
                            {errors.expense_id.message}
                          </div>
                        )}
                      </div>

                      {/* Travel or Subject */}
                      {isTravel ? (
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">
                            Travel <span className="text-danger">*</span>
                          </label>
                          <select
                            className={`form-select ${errors.employeeinfo_has_travel_id ? "is-invalid" : ""}`}
                            {...register("employeeinfo_has_travel_id", {
                              validate: (v) =>
                                !isTravel || !!v || "Please select a travel",
                            })}
                          >
                            <option value="">Choose Travel</option>
                            {formOptions.travels.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.date_from} → {t.date_to}
                                {t.preferred_location
                                  ? ` (${t.preferred_location})`
                                  : ""}
                                {" — "}
                                {t.way}
                              </option>
                            ))}
                          </select>
                          {errors.employeeinfo_has_travel_id && (
                            <div className="invalid-feedback">
                              {errors.employeeinfo_has_travel_id.message}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="col-md-4">
                          <label className="form-label fw-semibold">
                            Subject <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            autoComplete="off"
                            className={`form-control ${errors.subject ? "is-invalid" : ""}`}
                            {...register("subject", {
                              validate: (v) =>
                                isTravel || !!v.trim() || "Subject is required",
                            })}
                          />
                          {errors.subject && (
                            <div className="invalid-feedback">
                              {errors.subject.message}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Authorised By */}
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">
                          Authorised By <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-select ${errors.authorised_by ? "is-invalid" : ""}`}
                          {...register("authorised_by", {
                            required: "Authorised person is required",
                          })}
                        >
                          <option value="">Select Authoriser</option>
                          {formOptions.users.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                        {errors.authorised_by && (
                          <div className="invalid-feedback">
                            {errors.authorised_by.message}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Bill items table ─────────────────────────────────── */}
                    <div className="table-responsive mb-3">
                      <table className="table table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 50 }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => append(defaultRow())}
                                title="Add Row"
                              >
                                <i className="ti ti-plus" />
                              </button>
                            </th>
                            <th>Bill Date</th>
                            <th>Type</th>
                            <th>Remarks</th>
                            <th>File (jpg/jpeg/png/gif/pdf)</th>
                            <th>Amount</th>
                            <th>Currency</th>
                            <th>Conv. Rate</th>
                            <th>
                              Conv. Amount{" "}
                              <span className="text-primary fw-bold">
                                (Total: {totalAmount.toLocaleString()})
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((field, index) => (
                            <BillRow
                              key={field.id}
                              index={index}
                              isFirst={index === 0}
                              control={control}
                              register={register}
                              errors={errors}
                              watch={watch}
                              setValue={setValue}
                              reimbursementTypes={
                                formOptions.reimbursementTypes
                              }
                              currencies={formOptions.currencies}
                              onRemove={() => remove(index)}
                              onFetchRate={fetchConversionRate}
                              onOpenCamera={() => setCameraRowIndex(index)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* ── Other file ───────────────────────────────────────── */}
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Other File (pdf/jpeg/xls/word)
                        </label>
                        <div className="input-group">
                          <Controller
                            name="otherFileName"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="text"
                                className="form-control"
                                readOnly
                                value={field.value}
                                placeholder="No file chosen"
                              />
                            )}
                          />
                          <label className="btn btn-outline-secondary mb-0">
                            Browse
                            <input
                              type="file"
                              className="d-none"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                setValue("otherFile", file);
                                setValue("otherFileName", file.name);
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </form>
                )}
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeModal}
                >
                  Close
                </button>
                <button
                  type="submit"
                  form="reimbursement-form"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {editId ? "Update" : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Camera modal ─────────────────────────────────────────────────────
          Lives at the top level (outside the form modal) and is shared by all
          rows. cameraRowIndex !== null means "open, and target that row". */}
      <CameraCapture
        open={cameraRowIndex !== null}
        onClose={() => setCameraRowIndex(null)}
        onCapture={handleCameraCapture}
      />
    </>
  );
}

// ─── BillRow sub-component ────────────────────────────────────────────────────
function BillRow({
  index,
  isFirst,
  control,
  register,
  errors,
  watch,
  setValue,
  reimbursementTypes,
  currencies,
  onRemove,
  onFetchRate,
  onOpenCamera,
}) {
  const rowErrors = errors?.rows?.[index] || {};
  const currentAmount = watch(`rows.${index}.amount`);
  const currentCurrency = watch(`rows.${index}.currency_id`);
  const billFileName = watch(`rows.${index}.billFileName`);
  const fileId = watch(`rows.${index}.file_id`);
  const billFile = watch(`rows.${index}.billFile`);

  // ── Thumbnail preview for freshly attached / captured image files ──────────
  //   Only applies to actual File objects that are images. Existing edit-mode
  //   files (file_id set, billFile null) don't get a thumbnail here because
  //   fetching them requires an auth header — the filename in the input covers
  //   that case. Object URLs are revoked on change/unmount to avoid leaks.
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    if (billFile instanceof File && billFile.type.startsWith("image/")) {
      const url = URL.createObjectURL(billFile);
      setThumbUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setThumbUrl(null);
  }, [billFile]);

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setValue(`rows.${index}.amount`, val, { shouldDirty: true });
    // Reset currency-dependent fields when amount changes so user re-selects currency
    setValue(`rows.${index}.currency_id`, "");
    setValue(`rows.${index}.conversion_rate`, "");
    setValue(`rows.${index}.entity_amount`, "");
  };

  const handleCurrencyChange = (e) => {
    const cid = e.target.value;
    setValue(`rows.${index}.currency_id`, cid, { shouldDirty: true });
    if (cid && currentAmount) {
      onFetchRate(index, cid, currentAmount);
    }
  };

  return (
    <tr>
      {/* Add / Remove */}
      <td>
        {isFirst ? (
          <span className="text-muted">—</span>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={onRemove}
            title="Remove Row"
          >
            <i className="ti ti-minus" />
          </button>
        )}
      </td>

      {/* Bill Date */}
      <td>
        <input
          type="date"
          style={{ minWidth: 130 }}
          className={`form-control form-control-sm ${rowErrors.bill_date ? "is-invalid" : ""}`}
          {...register(`rows.${index}.bill_date`, { required: "Required" })}
        />
        {rowErrors.bill_date && (
          <div className="invalid-feedback">{rowErrors.bill_date.message}</div>
        )}
      </td>

      {/* Reimbursement Type */}
      <td>
        <select
          style={{ minWidth: 120 }}
          className={`form-select form-select-sm ${rowErrors.reimbursement_type_id ? "is-invalid" : ""}`}
          {...register(`rows.${index}.reimbursement_type_id`, {
            required: "Required",
          })}
        >
          <option value="">Select</option>
          {reimbursementTypes.map((rt) => (
            <option key={rt.id} value={rt.id}>
              {rt.name}
            </option>
          ))}
        </select>
        {rowErrors.reimbursement_type_id && (
          <div className="invalid-feedback">
            {rowErrors.reimbursement_type_id.message}
          </div>
        )}
      </td>

      {/* Remarks */}
      <td>
        <input
          type="text"
          autoComplete="off"
          style={{ minWidth: 100 }}
          className={`form-control form-control-sm ${rowErrors.remarks ? "is-invalid" : ""}`}
          {...register(`rows.${index}.remarks`, { required: "Required" })}
        />
        {rowErrors.remarks && (
          <div className="invalid-feedback">{rowErrors.remarks.message}</div>
        )}
      </td>

      {/* Bill File */}
      <td>
        <div className="input-group input-group-sm" style={{ minWidth: 210 }}>
          <input
            type="text"
            className="form-control"
            readOnly
            value={billFileName || ""}
            placeholder="Choose file"
          />
          <label className="btn btn-outline-secondary mb-0 py-0">
            Browse
            <Controller
              name={`rows.${index}.billFile`}
              control={control}
              rules={{
                validate: (v) => !!v || !!fileId || "Bill file is required",
              }}
              render={() => (
                <input
                  type="file"
                  className="d-none"
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setValue(`rows.${index}.billFile`, file, {
                      shouldValidate: true,
                    });
                    setValue(`rows.${index}.billFileName`, file.name);
                  }}
                />
              )}
            />
          </label>

          {/* NEW: capture bill photo with the device camera */}
          <button
            type="button"
            className="btn btn-outline-primary py-0"
            onClick={onOpenCamera}
            title="Capture with camera"
          >
            <i className="ti ti-camera" />
          </button>
        </div>

        {/* Thumbnail preview of the attached / captured image */}
        {thumbUrl && (
          <div className="mt-1">
            <img
              src={thumbUrl}
              alt="Bill preview"
              style={{
                maxHeight: 60,
                maxWidth: 90,
                objectFit: "cover",
                border: "1px solid #dee2e6",
                borderRadius: 4,
              }}
            />
          </div>
        )}

        {rowErrors.billFile && (
          <div className="text-danger" style={{ fontSize: "0.75rem" }}>
            {rowErrors.billFile.message}
          </div>
        )}
      </td>

      {/* Amount */}
      <td>
        <input
          type="number"
          min="0"
          step="0.01"
          style={{ minWidth: 80 }}
          className={`form-control form-control-sm ${rowErrors.amount ? "is-invalid" : ""}`}
          {...register(`rows.${index}.amount`, {
            required: "Required",
            min: { value: 0.01, message: "Must be > 0" },
          })}
          onChange={handleAmountChange}
        />
        {rowErrors.amount && (
          <div className="invalid-feedback">{rowErrors.amount.message}</div>
        )}
      </td>

      {/* Currency */}
      <td>
        <select
          style={{ minWidth: 120 }}
          className={`form-select form-select-sm ${rowErrors.currency_id ? "is-invalid" : ""}`}
          value={currentCurrency}
          {...register(`rows.${index}.currency_id`, { required: "Required" })}
          onChange={handleCurrencyChange}
        >
          <option value="">Select</option>
          {currencies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.symbol} - {c.name}
            </option>
          ))}
        </select>
        {rowErrors.currency_id && (
          <div className="invalid-feedback">
            {rowErrors.currency_id.message}
          </div>
        )}
      </td>

      {/* Conversion Rate */}
      <td>
        <input
          type="number"
          step="0.0001"
          style={{ minWidth: 80 }}
          className={`form-control form-control-sm ${rowErrors.conversion_rate ? "is-invalid" : ""}`}
          {...register(`rows.${index}.conversion_rate`, {
            required: "Required",
          })}
        />
        {rowErrors.conversion_rate && (
          <div className="invalid-feedback">
            {rowErrors.conversion_rate.message}
          </div>
        )}
      </td>

      {/* Converted Amount (read-only) */}
      <td>
        <input
          type="number"
          readOnly
          style={{ minWidth: 90 }}
          className="form-control form-control-sm bg-light"
          {...register(`rows.${index}.entity_amount`)}
        />
      </td>
    </tr>
  );
}

// ─── CameraCapture sub-component ──────────────────────────────────────────────
//   Opens the device camera (rear camera preferred via facingMode), lets the
//   user capture a frame, preview it, retake, or accept. On accept it hands back
//   a File object identical in shape to a file-picked image, so the rest of the
//   form and the backend are unaffected.
//
//   NOTE: getUserMedia only works in a secure context — HTTPS or localhost.
//   Over plain http on a LAN IP it will throw, and we surface a helpful message.
function CameraCapture({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null); // { url, file }

  // Start the stream when opened; stop + release on close/unmount.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setError("");
    setPreview(null);

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }, // rear camera on phones
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) {
        console.error("[CameraCapture] getUserMedia ERROR:", err);
        setError(
          "Unable to access the camera. Allow camera permission in your browser, and note the camera only works over HTTPS (or localhost). You can still use Browse to upload a file.",
        );
      }
    })();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `bill-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        // Keep the stream live behind the preview so "Retake" is instant.
        setPreview({ url: URL.createObjectURL(blob), file });
      },
      "image/jpeg",
      0.9, // quality
    );
  };

  const retake = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null); // live video is still mounted, just re-shows
  };

  const usePhoto = () => {
    if (!preview) return;
    onCapture(preview.file);
    URL.revokeObjectURL(preview.url);
    setPreview(null);
    stopStream();
    onClose();
  };

  const handleClose = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    stopStream();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.7)", zIndex: 1080 }} // above form modal
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Capture Bill Photo</h5>
            <button type="button" className="btn-close" onClick={handleClose} />
          </div>

          <div className="modal-body text-center">
            {error ? (
              <div className="alert alert-danger mb-0">{error}</div>
            ) : (
              <div style={{ position: "relative" }}>
                {/* Video stays mounted so the ref never goes null on retake */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-100 rounded"
                  style={{
                    maxHeight: 360,
                    background: "#000",
                    display: preview ? "none" : "block",
                  }}
                />
                {preview && (
                  <img
                    src={preview.url}
                    alt="Captured bill"
                    className="img-fluid rounded"
                    style={{ maxHeight: 360 }}
                  />
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            {!error && !preview && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={takePhoto}
              >
                <i className="ti ti-camera me-1" /> Capture
              </button>
            )}
            {!error && preview && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={retake}
                >
                  <i className="ti ti-refresh me-1" /> Retake
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={usePhoto}
                >
                  <i className="ti ti-check me-1" /> Use Photo
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}